import { IRegistry } from '../../../Assembler/dist/registry/iface/iRegistry.js';
import type { IData } from '../../../Assembler/dist/registry/iface/iData.js';
import type { IClientData } from '../../../Assembler/dist/registry/iface/iClientData.js';
import type { IView } from '../../../Assembler/dist/registry/iface/iView.js';
import type { IAction } from '../../../Assembler/dist/registry/iface/iAction.js';
import type { IAppSite } from '../../../Assembler/dist/registry/iface/iAppSite.js';
import type { IDataReloadable } from '../../../Assembler/dist/registry/iface/iDataReloadable.js';
import type { IDataSource } from '../../../Assembler/dist/registry/iface/iDataSource.js';
import { ActionResultDataSource } from './dataSource/actionResultDataSource.js';
import { TimeData } from './dynamicData/timeData.js';
import { CounterData } from './dynamicData/counterData.js';
import { TimeClientData } from './dynamicClientData/timeClientData.js';
import { CounterClientData } from './dynamicClientData/counterClientData.js';
import { CounterManager } from './dataReloadable/counterManager.js';
import { CounterAction } from './dynamicAction/counterAction.js';
import { ReplaceAppSite } from './dynamicAppSite/replaceAppSite.js';
import { Logger } from '../../../Arshu/dist/logger/logger.js';

/**
 * Base implementation of IRegistry that registers the default data, action and AppSite providers.
 *
 * View handlers are NOT registered here — they live in RegisterView so a client host can register views
 * without taking on this registrar's server-oriented handlers.
 */
export class RegisterBase extends IRegistry {
    private readonly _counterManager: CounterManager | null;

    /**
     * Stores the optional App_Data-backed counter manager used to initialize the counter handlers.
     * A client host registers without one: counters are server state, so the browser-side registrar
     * keeps the parameterless form and the counter handlers fall back to per-merge transient values.
     */
    constructor(counterManager: CounterManager | null = null) {
        super();
        this._counterManager = counterManager;
    }

    // #region IRegistry implementation

    /**
     * No view providers — the core views (BaseView, RuleView) are registered by RegisterView.
     */
    registerView(dynamicViewList: Map<string, IView>): void {
        void dynamicViewList;
    }

    _registeringData: boolean = false;

    /**
     * Registers data providers to the dynamic data list
     */
    registerData(dynamicDataList: Map<string, IData>): void {
        try {
            if (this._registeringData === false) {
                this._registeringData = true;
                let key = '';

                key = 'TIMEDATA';
                dynamicDataList.set(key, new TimeData());

                key = 'COUNTERDATA';
                const counterData = new CounterData();
                if (this._counterManager !== null) {
                    counterData.initialize(this._counterManager);
                }
                dynamicDataList.set(key, counterData);

                this._registeringData = false;
            }
        } catch (ex) {
            Logger.error((ex as Error).message, 'RegistryBase');
        }
    }

    _registeringClientData: boolean = false;

    /**
     * Registers the core server-side CLIENT-DATA preparer handlers into the supplied registry map.
     * These produce the raw island a browser handler projects client-side (via applyDynamicClientData);
     * they never run on the pure server IData path.
     *
     * They live HERE rather than in RegisterServer because a preparer belongs in the same registry root as
     * the data provider whose output it mirrors, and TimeData/CounterData are registered by this registrar.
     * The counter preparer takes the same optional CounterManager as CounterData, so a client host
     * registering without one gets transient-or-base resolution in both tiers alike.
     */
    registerClientData(dynamicClientDataList: Map<string, IClientData>): void {
        try {
            if (this._registeringClientData === false) {
                this._registeringClientData = true;
                let key = '';

                key = 'TIMECLIENTDATA';
                dynamicClientDataList.set(key, new TimeClientData());

                key = 'COUNTERCLIENTDATA';
                const counterClientData = new CounterClientData();
                if (this._counterManager !== null) {
                    counterClientData.initialize(this._counterManager);
                }
                dynamicClientDataList.set(key, counterClientData);

                this._registeringClientData = false;
            }
        } catch (ex) {
            Logger.error((ex as Error).message, 'RegistryBase');
        }
    }

    _registeringAction: boolean = false;

    /**
     * Registers action providers to the dynamic action list
     */
    registerAction(dynamicActionList: Map<string, IAction>): void {
        try {
            if (this._registeringAction === false) {
                this._registeringAction = true;
                let key = '';

                key = 'COUNTERACTION';
                if (dynamicActionList.has(key) === true) {
                    dynamicActionList.delete(key);
                }
                const counterAction = new CounterAction();
                if (this._counterManager !== null) {
                    counterAction.initialize(this._counterManager);
                }
                dynamicActionList.set(key, counterAction);

                this._registeringAction = false;
            }
        } catch (ex) {
            Logger.error((ex as Error).message, 'RegistryBase');
        }
    }

    _registeringAppSite: boolean = false;

    /**
     * Registers the core dynamic AppSite handlers into the supplied registry map.
     */
    registerAppSite(dynamicAppSiteList: Map<string, IAppSite>): void {
        try {
            if (this._registeringAppSite === false) {
                this._registeringAppSite = true;

                const key = 'REPLACEAPPSITE';
                if (dynamicAppSiteList.has(key) === true) {
                    dynamicAppSiteList.delete(key);
                }
                dynamicAppSiteList.set(key, new ReplaceAppSite());

                this._registeringAppSite = false;
            }
        } catch (ex) {
            Logger.error((ex as Error).message, 'RegistryBase');
        }
    }

    /**
     * Registers base app-data backed runtime services.
     *
     * Registered under the manager's own store name, NOT under each counter AppSite: one CounterManager
     * serves every counter AppSite from a single flat CounterData.json keyed by AppSite, and this
     * registrar runs before any host has necessarily loaded the AppSite config. A baseline restore
     * reaches it through RegistryStore.reloadAllAppData, which reloads every service reading the one
     * shared store rather than guessing which AppSite key owns it.
     */
    registerReloadable(appDataReloadables: Map<string, IDataReloadable[]>): void {
        if (this._counterManager === null) {
            return;
        }

        let reloadables = appDataReloadables.get(this._counterManager.appSite);
        if (reloadables === undefined) {
            reloadables = [];
            appDataReloadables.set(this._counterManager.appSite, reloadables);
        }
        reloadables.push(this._counterManager);
        Logger.flow(`event=appDataAdd appSite=${this._counterManager.appSite} handlerType=CounterManager callbacksSize=${reloadables.length}`, 'Registry');
    }

    /**
     * Registers the generic action-result capture source ("actionresult:<Field>") — available to every AppSite.
     */
    registerDataSource(dataSources: Map<string, IDataSource>): void {
        const source = new ActionResultDataSource();
        dataSources.set(source.scheme.toUpperCase(), source);
    }

    // #endregion
}
